import {
  ActivityCategory,
  ActivityDifficulty,
  ActivitySortField,
  ActivitySortOrder,
  GetActivitiesQueryVariables,
} from "@/graphql/graphql";

export interface ActivitiesQueryState {
  page: number;
  pageSize: number;
  titleSearch?: string;
  contentSearch?: string;
  categories?: ActivityCategory[];
  typeKeys?: string[];
  difficulties?: ActivityDifficulty[];
  sortField?: ActivitySortField;
  sortDirection?: ActivitySortOrder;
}

export const initialActivitiesQueryState = (): ActivitiesQueryState => ({
  page: 0,
  pageSize: 20,
});

export const buildActivitiesVariables = (
  {
    page,
    pageSize,
    titleSearch,
    contentSearch,
    categories,
    typeKeys,
    difficulties,
    sortField,
    sortDirection,
  }: ActivitiesQueryState,
  accountId?: number
): GetActivitiesQueryVariables => ({
  accountId,
  skip: page * pageSize,
  take: pageSize,
  sort: sortField &&
    sortDirection && {
      fieldName: sortField,
      direction: sortDirection,
    },
  filters:
    titleSearch || contentSearch || categories || difficulties || typeKeys
      ? {
          titleSearch: titleSearch,
          contentSearch: contentSearch,
          categories: categories,
          difficulties: difficulties,
          typeKeys: typeKeys,
        }
      : undefined,
});

export type ActivitiesQueryAction =
  | { type: "SET_PAGE"; page: number }
  | { type: "SET_PAGE_SIZE"; pageSize: number }
  | { type: "SET_TITLE_SEARCH"; titleSearch: string }
  | { type: "SET_CONTENT_SEARCH"; contentSearch: string }
  | { type: "SET_CATEGORIES"; categories: ActivityCategory[] }
  | { type: "SET_TYPE_KEYS"; typeKeys: string[] }
  | { type: "SET_DIFFICULTIES"; difficulties: ActivityDifficulty[] }
  | { type: "TOGGLE_SORT"; field: ActivitySortField }
  | { type: "CLEAR_FILTERS" }
  | { type: "RESET" };

export const activitiesQueryReducer = (
  state: ActivitiesQueryState,
  action: ActivitiesQueryAction
): ActivitiesQueryState => {
  switch (action.type) {
    case "SET_PAGE":
      return { ...state, page: action.page };
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.pageSize, page: 0 };
    case "SET_TITLE_SEARCH":
      return { ...state, titleSearch: action.titleSearch, page: 0 };
    case "SET_CONTENT_SEARCH":
      return { ...state, contentSearch: action.contentSearch, page: 0 };
    case "SET_CATEGORIES":
      return { ...state, categories: action.categories, page: 0 };
    case "SET_TYPE_KEYS":
      return { ...state, typeKeys: action.typeKeys, page: 0 };
    case "SET_DIFFICULTIES":
      return { ...state, difficulties: action.difficulties, page: 0 };
    case "TOGGLE_SORT": {
      // Toggles between ascending, descending and none.
      let sortDirection: ActivitiesQueryState["sortDirection"];

      if (state.sortField !== action.field || !state.sortDirection) {
        sortDirection = ActivitySortOrder.Asc;
      } else if (state.sortDirection === ActivitySortOrder.Asc) {
        sortDirection = ActivitySortOrder.Desc;
      }

      return { ...state, sortField: sortDirection && action.field, sortDirection, page: 0 };
    }
    case "CLEAR_FILTERS":
      return {
        ...initialActivitiesQueryState(),
        pageSize: state.pageSize,
        sortField: state.sortField,
        sortDirection: state.sortDirection,
      };
    case "RESET":
      return { ...initialActivitiesQueryState(), pageSize: state.pageSize };
    default:
      return state;
  }
};
